import { memo } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { IoStar, IoAlertCircle } from 'react-icons/io5';
import ProductCardActions from './ProductCardActions';
import { Product } from '../types';
import { isProductAvailable, isFreeProduct } from '../utils/productUtils';

type ProductCardProps = {
  product: Product;
};

const ProductCard = memo(({ product }: ProductCardProps) => {
  const isAvailable = isProductAvailable(product);
  const primaryImageUrl = Array.isArray(product.image_url) ? product.image_url[0] : product.image_url;
  const cartProduct: Product = {
    id: product.id,
    name: product.name,
    slug: product.slug,
    price: product.price,
    image_url: primaryImageUrl || '',
    benefits: [],
    compare_at_price: product.compare_at_price,
    productCategoryId: product.productCategoryId,
    metaData: product.metaData,
    tags: product.tags,
    isVisible: product.isVisible,
  };

  // Helper to convert text to Title Case
  const titleCase = (str: string) => {
    if (!str) return '';
    return str.toLowerCase().split(' ').map(word => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    }).join(' ');
  };

  const productHref =
    product.slug === 'astroji-premium-aghori-evil-eye-locket-9c1efe'
      ? '/evil-eye/aghori-locket'
      : `/product/${product.slug}`;

  return (
    <div className={`group/card ui-card ui-card-hover relative flex h-full flex-col overflow-hidden ${!isAvailable ? 'opacity-80' : ''}`}>

      {/* Image Section - Square Aspect Ratio */}
      <Link prefetch={false} href={productHref} className="relative block aspect-square overflow-hidden bg-gray-50">

        {/* Temporarily Unavailable Badge - Top Left */}
        {!isAvailable && (
          <div className="absolute left-2 top-2 z-10 flex items-center gap-1 rounded-full bg-red-500 px-2 py-1 text-[10px] font-bold text-white">
            <IoAlertCircle size={12} />
            Out of Stock
          </div>
        )}

        {/* Rating Badge - Top Right */}
        {product.rating != null && (
          <div className="absolute right-2 top-2 z-10 flex items-center gap-1 rounded-full border border-gray-100 bg-white/90 px-2 py-1 backdrop-blur-sm">
            <IoStar className="text-yellow-400" size={10} />
            <span className="text-[10px] font-bold text-gray-700">{product.rating.toFixed(1)}</span>
          </div>
        )}

        {primaryImageUrl && (
          <Image
            src={primaryImageUrl}
            alt={product.name ? `${product.name} - Authentic AstroJi Spiritual Product` : 'AstroJi Authentic Spiritual Product'}
            fill
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
            unoptimized={process.env.NODE_ENV === 'development'}
            className={`object-cover transition-transform duration-300 ease-out group-hover/card:scale-[1.025] ${!isAvailable ? 'grayscale-[30%]' : ''}`}
          />
        )}

        {/* Subtle Gradient Overlay on Hover */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/5 to-transparent opacity-0 transition-opacity duration-200 group-hover/card:opacity-100" />
      </Link>

      <div className="flex flex-grow flex-col p-3">
        {/* Title - Fixed min-height to prevent grid layout shifts between 1-line and 2-line titles */}
        <Link prefetch={false} href={productHref} className="mb-2 block min-h-[2.5rem] sm:min-h-[2.75rem]">
          <h3 className="line-clamp-2 font-sans text-sm font-semibold leading-snug text-gray-900 transition-colors duration-200 group-hover/card:text-primary sm:text-base">
            {titleCase(product.name)}
          </h3>
        </Link>

        {/* Price Section */}
        <div className="mt-auto pt-1">
          <div className="mb-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="text-lg font-bold text-gray-900">₹{isFreeProduct(product) ? '0' : product.price.toLocaleString()}</span>
            {(product.compare_at_price || (isFreeProduct(product) && product.price > 0)) && (
              <span className="text-xs text-gray-500 line-through decoration-gray-400">₹{(product.compare_at_price || product.price).toLocaleString()}</span>
            )}
          </div>

          {/* Action Button */}
          <div className="mt-auto">
            <div className="mt-3 flex items-center justify-between">
              {!isAvailable ? (
                // Unavailable product - show disabled state
                <div className="flex min-h-10 w-full items-center justify-center gap-2 rounded-lg bg-gray-100 px-2 py-2 text-xs font-semibold text-gray-500 sm:min-h-11 sm:text-sm">
                  <IoAlertCircle size={16} />
                  Out of Stock
                </div>
              ) : (
                <ProductCardActions product={cartProduct} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

ProductCard.displayName = 'ProductCard';

export default ProductCard;
